import React, {useEffect, useState} from 'react';
import {DataGrid, GridColDef, GridPaginationModel, GridRowParams, GridSortModel} from '@mui/x-data-grid';
import {fetchAdminPage, fetchUserPage} from '../../api/endpoints';
import UserDto from '../../interfaces/UserDto';
import {isRight} from 'fp-ts/lib/Either';
import Modal from 'react-modal';
import AdminUserModal from './AdminUserModal';


const AdminUsers: React.FC = () => {
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [filterQuery, setFilterQuery] = useState('');
    const [adminFilter, setAdminFilter] = useState(false);
    const [lastSearched, setLastSearched] = useState('');
    const [selectedRow, setSelectedRow] = useState<UserDto | null>(null);

    const [rows, setRows] = useState<UserDto[]>([]);
    const [totalRows, setTotalRows] = useState<number>(0);
    const [sortModel, setSortModel] = useState<GridSortModel>([
        {
            field: "updatedAt",
            sort: "desc"
        }
    ]);
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
        page: 0,
        pageSize: 10,
    });

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'ID', width: 50, disableColumnMenu: true },
        { field: 'firstName', headerName: 'First Name', width: 120, disableColumnMenu: true },
        { field: 'lastName', headerName: 'Last Name', width: 120, disableColumnMenu: true },
        { field: 'email', headerName: 'Email', width: 200, disableColumnMenu: true },
        { field: 'createdAt', headerName: 'Created At', width: 200, disableColumnMenu: true },
        { field: 'updatedAt', headerName: 'Updated At', width: 200, disableColumnMenu: true },
        { field: 'isAdmin', headerName: 'Admin', width: 100, disableColumnMenu: true, sortable: false }
    ];

    const fetchData = async (search: string, paginationModel: GridPaginationModel, sortModel: GridSortModel) => {
        setLoadingUsers(true);
        const page = paginationModel.page;
        const pageSize = paginationModel.pageSize;
        const sortBy = sortModel[0]?.field || "updatedAt";
        const order = sortModel[0]?.sort || "desc";
        const usersDataResponseEither = adminFilter ? await fetchAdminPage(page, pageSize, search, sortBy, order) : await fetchUserPage(page, pageSize, search, sortBy, order);
        if (isRight(usersDataResponseEither)) {
            const { content, total, pageable } = usersDataResponseEither.right;
            setRows(content);
            setTotalRows(total);
        } else {
            console.error(usersDataResponseEither.left)
        }

        setLoadingUsers(false);
    };

    useEffect(() => {
        fetchData(lastSearched, paginationModel, sortModel);
    }, [paginationModel, sortModel]);

    useEffect(() => {
        setPaginationModel({
            page: 0,
            pageSize: 10,
        })
        setSortModel([
            {
                field: "updatedAt",
                sort: "desc"
            }
        ])

        fetchData(lastSearched, paginationModel, sortModel);

    }, [adminFilter]);

    const handlePaginationModelChange = (model: GridPaginationModel) => {
        setPaginationModel(model);
    };

    const handleSortModelChange = (newSortModel: GridSortModel) => {
        setSortModel(newSortModel);
    };

    const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFilterQuery(e.target.value);
    };

    const handleFilterSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        fetchData(filterQuery, paginationModel, sortModel);
        setLastSearched(filterQuery);
    }

    const handleRowClick = (params: GridRowParams) => {
        setSelectedRow(params.row as UserDto);
    };

    return (
        <div className='card card-top' style={{ minHeight: 500, width: '100%' }} >
            <div className='text-l'>Users</div>
            <div className='card-item'>
                <form onSubmit={handleFilterSubmit} className='card-item'>
                    <div className="row">
                        <input type="text" value={filterQuery} onChange={handleQueryChange}
                            placeholder="Type to filter by first name, last name, and email"
                            className='admin-search'
                        />
                        <button type="submit" className="admin-action-button">Filter</button>
                        <div className='gap-s' />
                        <div className='text-xs'>View Admins&nbsp;</div>
                        <input type="checkbox" checked={adminFilter} onChange={() => setAdminFilter(!adminFilter)} className='admin-checkbox' />
                    </div>
                </form>
            </div>

            {loadingUsers ?
                <div className="loading-spinner"></div>
                :
                <DataGrid
                    rows={rows}
                    columns={columns}
                    paginationMode="server"
                    paginationModel={paginationModel}
                    onPaginationModelChange={handlePaginationModelChange}
                    sortingMode="server"
                    sortModel={sortModel}
                    onSortModelChange={handleSortModelChange}
                    rowCount={totalRows}
                    pageSizeOptions={[5, 10, 20]}
                    onRowClick={handleRowClick}
                    className='admin-data-grid'

                />
            }
            <Modal isOpen={selectedRow !== null} onRequestClose={() => setSelectedRow(null)} style={{
                content: {
                    display: 'flex',
                    flexDirection: 'column',
                    maxWidth: '1000px',
                    margin: 'auto',
                    top: '125px',
                    bottom: '40px',
                    padding: '40px',
                    borderRadius: '15px'
                },
                overlay: {
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                }
            }}
                appElement={document.getElementById('root') || undefined}
            >
                {selectedRow && (
                    <AdminUserModal
                        user={selectedRow}
                        closeModal={() => setSelectedRow(null)}
                        refetchUsers={() => fetchData(lastSearched, paginationModel, sortModel)}
                    />
                )}
            </Modal>
        </div>
    );
};

export default AdminUsers;
